import * as THREE from 'three';

export type ScoutState = {
  enabled: boolean;
  /** blossom ids (node ids) that are considered "far" from the main orchestrator */
  farBlossoms: string[];
  /** current target blossom id */
  targetId: string | null;
  /** ids already evaluated */
  visited: Set<string>;
  /** pseudo-random seed */
  seed: number;
  /** when last evaluation happened (ms) */
  lastDecisionAt: number;
};

export function createScoutMesh() {
  const group = new THREE.Group();
  group.name = 'auto-blossom-scout';

  const core = new THREE.Mesh(
    new THREE.SphereGeometry(6.2, 20, 20),
    new THREE.MeshBasicMaterial({
      color: new THREE.Color('#60a5fa'),
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    })
  );
  core.userData.__isScoutCore = true;
  group.add(core);

  const halo = new THREE.Mesh(
    new THREE.SphereGeometry(12, 16, 16),
    new THREE.MeshBasicMaterial({
      color: new THREE.Color('#60a5fa'),
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  );
  halo.userData.__isScoutHalo = true;
  group.add(halo);

  return group;
}

function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pickNextTarget(state: ScoutState) {
  if (!state.farBlossoms.length) return null;
  const rng = mulberry32(state.seed);

  // Prefer unvisited, else any.
  const unvisited = state.farBlossoms.filter((id) => !state.visited.has(id));
  const pool = unvisited.length ? unvisited : state.farBlossoms;

  const idx = Math.floor(rng() * pool.length);
  return pool[idx] || null;
}

export function shouldForkBlossom(state: ScoutState, blossomId: string, nowMs: number) {
  // Keep it simple + deterministic:
  // - only decide once per blossom
  // - 35% chance to fork
  if (state.visited.has(blossomId)) return false;

  const rng = mulberry32(hashToSeed(`${state.seed}:${blossomId}`));
  const chance = rng();

  // avoid forking too frequently
  const cooldownOk = nowMs - state.lastDecisionAt > 2500;
  return cooldownOk && chance < 0.35;
}

export function hashToSeed(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function makeForkQuestion(blossomLabel: string) {
  const base = (blossomLabel || 'Idea').replace(/\s+/g, ' ').trim();
  const trimmed = base.length > 72 ? base.slice(0, 72) + '…' : base;
  return `Is "${trimmed}" worth investigating as its own line of inquiry?`;
}
